import { useMutation, useQuery } from '@apollo/client/react';
import { datadogRum } from '@datadog/browser-rum';
import { Paper, Slide } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useSnackbar } from '../../contexts/snackbarContext';
import {
  GenerateSignedUrlDocument,
  GetCoachChatRoomDocument,
  GetCoachChatTotalUnreadDocument,
  MarkChatReadDocument,
  Message as MessageType,
  Prefix,
  SendMessageDocument,
} from '../../graphql/types';
import useMixpanel from '../../hooks/useMixpanel';
import {
  coachChatSubscribeToMore,
  coachChatTotalUnreadSubscribeToMore,
  mergeSentMessageIntoCache,
} from '../../utils/chat/merge';
import {
  getChatTrackingEventCategory,
  getVideoContentIdFromUrl,
  isVideoContentUrl,
} from '../../utils/chatTracking';
import { computeUrl } from '../../utils/image';
import {
  generateImagePreview,
  resizeImage,
  UploadedPhoto,
  uploadPhotoToS3,
  validateImageFile,
} from '../../utils/photoUpload';
import ChatFab from './components/ChatFab';
import ChatHeader from './components/ChatHeader';
import ChatInput from './components/ChatInput';
import ChatList from './components/ChatList';
import ChatMessages from './components/ChatMessages';
import type { ChatRoomNode, ChatRoomUser } from './types';

interface ChatProps {
  className?: string;
}

const ChatContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.baseUnit * 2,
  right: theme.baseUnit * 2,
  width: 380,
  maxHeight: 600,
  borderRadius: theme.baseUnit * 2,
  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
  border: `1px solid ${theme.colors.alias.mainSurfaceDivider}`,
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.colors.alias.mainBackground,
  overflow: 'hidden',
}));

const CoachChat: React.FC<ChatProps> = ({ className }) => {
  const track = useMixpanel();
  const snackbar = useSnackbar();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevMessagesLengthRef = useRef(0);

  const [isExpanded, setIsExpanded] = useState(false);
  const [messagesVisible, setMessagesVisible] = useState(false);
  const [selectedChatRoom, setSelectedChatRoom] = useState<ChatRoomNode | null>(
    null,
  );
  const [messageText, setMessageText] = useState('');
  const [currentUserId, setCurrentUserId] = useState('');
  const [pendingPhotos, setPendingPhotos] = useState<UploadedPhoto[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);

  const { data, loading, error, subscribeToMore } = useQuery(
    GetCoachChatRoomDocument,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    return coachChatSubscribeToMore(subscribeToMore, selectedChatRoom?.id);
  }, [subscribeToMore, selectedChatRoom?.id]);

  const { data: totalUnreadData, subscribeToMore: subscribeToMoreTotalUnread } =
    useQuery(GetCoachChatTotalUnreadDocument, {
      fetchPolicy: 'cache-and-network',
    });

  useEffect(() => {
    return coachChatTotalUnreadSubscribeToMore(subscribeToMoreTotalUnread);
  }, [subscribeToMoreTotalUnread]);

  const [sendMessage, { loading: sendingMessage }] =
    useMutation(SendMessageDocument);
  const [markChatRead] = useMutation(MarkChatReadDocument);
  const [generateSignedUrl] = useMutation(GenerateSignedUrlDocument);

  const currentUser = data?.self;
  const coach = data?.self?.fitCoach;
  const allChatRooms = data?.self?.chatRooms?.nodes || [];
  const chatRooms = allChatRooms.filter(
    (room) => room.usersWithoutInput.nodes.length > 1,
  );
  const totalUnreadFromSubscription =
    totalUnreadData?.self?.coachData?.totalUnreadMessageCount;
  const totalUnreadCount =
    totalUnreadFromSubscription != null
      ? totalUnreadFromSubscription
      : chatRooms.reduce(
          (total, room) => total + room.messages.unreadMessageCount,
          0,
        );
  const currentChatRoom = selectedChatRoom
    ? chatRooms.find((room) => room.id === selectedChatRoom.id) ||
      selectedChatRoom
    : null;
  const messages = currentChatRoom?.messages?.nodes || [];
  const unreadCount = currentChatRoom?.messages?.unreadMessageCount || 0;

  const scrollToLatestMessage = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
        setMessagesVisible(true);
      }, 150);
    },
    [],
  );

  useEffect(() => {
    if (currentUser?.id) {
      setCurrentUserId(currentUser.id);
    }
  }, [currentUser]);

  useEffect(() => {
    if (isExpanded) {
      setMessagesVisible(false);
      scrollToLatestMessage('auto');
    } else {
      setMessagesVisible(false);
    }
  }, [isExpanded, scrollToLatestMessage]);

  useEffect(() => {
    if (
      isExpanded &&
      selectedChatRoom &&
      messages.length > 0 &&
      unreadCount > 0
    ) {
      const lastMessage = messages[messages.length - 1];
      markChatRead({
        variables: {
          chatId: selectedChatRoom.id,
          lastMessageRead: lastMessage.createdAt,
        },
        update(cache, _, { variables: vars }) {
          if (!vars) {
            return;
          }
          cache.modify({
            id: cache.identify({
              __typename: 'ChatRoom',
              id: vars.chatId,
            }),
            fields: {
              messages(existing) {
                return { ...existing, unreadMessageCount: 0 };
              },
              lastMessageRead() {
                return vars.lastMessageRead;
              },
            },
          });
        },
      });
    }
  }, [isExpanded, selectedChatRoom, messages, unreadCount, markChatRead]);

  // Scroll to bottom when a new message arrives via subscription.
  // Guarded by messagesVisible so it doesn't fire during the initial open
  // (where the isExpanded effect handles the first scroll).
  useEffect(() => {
    const prevLength = prevMessagesLengthRef.current;
    prevMessagesLengthRef.current = messages.length;
    if (
      isExpanded &&
      selectedChatRoom &&
      messagesVisible &&
      messages.length > prevLength
    ) {
      scrollToLatestMessage();
    }
  }, [
    messages.length,
    isExpanded,
    selectedChatRoom,
    messagesVisible,
    scrollToLatestMessage,
  ]);

  const handleToggle = useCallback(() => {
    const opening = !isExpanded;
    setIsExpanded(opening);
    track('User Action', {
      event_name: isExpanded ? 'close_chat' : 'open_chat',
      event_type: 'screen interaction',
      event_category: getChatTrackingEventCategory(undefined),
      event_subcategory: 'chat',
      event_location: 'chat_widget',
    });
    if (opening) {
      track('Screen View', { page_name: 'chat_inbox' });
    }
  }, [isExpanded, track]);

  const handleChatSelect = useCallback(
    (chatRoom: ChatRoomNode) => {
      setSelectedChatRoom(chatRoom);
      setPendingPhotos([]);
      setMessageText('');
      const otherUser = chatRoom.usersWithoutInput.nodes.find(
        (u: ChatRoomUser) => u.id !== currentUserId,
      );
      track('User Action', {
        event_name: 'view_chat_detail',
        event_type: 'screen interaction',
        event_category: getChatTrackingEventCategory(undefined),
        event_subcategory: 'chat',
        event_location: 'chat_inbox',
        conversation_id: chatRoom.id,
        chat_id: chatRoom.id,
        coach_id: otherUser?.id,
      });
      track('Screen View', {
        page_name: 'chat',
        chat_id: chatRoom.id,
        coach_id: otherUser?.id,
      });
      setMessagesVisible(false);
      scrollToLatestMessage('auto');
    },
    [currentUserId, track, scrollToLatestMessage],
  );

  const handleBackToList = useCallback(() => {
    setSelectedChatRoom(null);
    setPendingPhotos([]);
    setMessageText('');
  }, []);

  const handlePhotoUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) {
        return;
      }

      const file = files[0];
      const validationError = validateImageFile(file);
      if (validationError) {
        setPhotoUploadError(validationError);
        return;
      }

      try {
        setUploadingPhoto(true);
        setPhotoUploadError(null);

        const resizedFile = await resizeImage(file, 1920, 1080, 0.8);
        const localUrl = await generateImagePreview(resizedFile);

        const { data: signedUrlData } = await generateSignedUrl({
          variables: {
            input: { contentType: resizedFile.type, prefix: Prefix.Uploads },
          },
        });

        if (!signedUrlData?.generateSignedUrl) {
          throw new Error('Failed to get signed URL');
        }

        await uploadPhotoToS3(
          resizedFile,
          signedUrlData.generateSignedUrl.signedUrl,
        );

        const uploadedPhoto: UploadedPhoto = {
          id: signedUrlData.generateSignedUrl.photo.id,
          preview: signedUrlData.generateSignedUrl.photo.preview,
          basePath: signedUrlData.generateSignedUrl.photo.basePath,
          file: resizedFile,
          localUrl,
        };
        setPendingPhotos((prev) => [...prev, uploadedPhoto]);

        track('User Action', {
          event_name: 'upload_photo',
          event_type: 'active engagement',
          event_category: getChatTrackingEventCategory(undefined),
          event_subcategory: 'chat',
          event_location: 'coach_chat_widget',
          conversation_id: selectedChatRoom?.id,
          chat_id: selectedChatRoom?.id,
          coach_id: coach?.id,
          file_size: resizedFile.size,
          file_type: resizedFile.type,
        });
      } catch (uploadError) {
        datadogRum.addError(uploadError);
        setPhotoUploadError('Failed to upload photo. Please try again.');
      } finally {
        setUploadingPhoto(false);
      }
    },
    [generateSignedUrl, track, coach?.id, selectedChatRoom?.id],
  );

  const handleRemovePhoto = useCallback((index: number) => {
    setPendingPhotos((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handlePhotoButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (
      (!messageText.trim() && pendingPhotos.length === 0) ||
      !selectedChatRoom ||
      sendingMessage
    ) {
      return;
    }

    try {
      const finalText =
        pendingPhotos.length > 0
          ? pendingPhotos
              .map((photo) => computeUrl(photo, { width: 2000, height: 2000 }))
              .join('\n')
          : messageText.trim();

      const optimisticId = uuid();
      const optimisticCreatedAt = new Date().toISOString();
      const selfInRoom = currentUser
        ? selectedChatRoom?.usersWithoutInput?.nodes?.find(
            (u) => u.id === currentUser.id,
          )
        : undefined;
      const optimisticMessage = {
        __typename: 'Message' as const,
        id: `optimistic/${optimisticId}`,
        text: finalText,
        createdAt: optimisticCreatedAt,
        user: currentUser
          ? {
              __typename: 'User' as const,
              id: currentUser.id,
              firstName: selfInRoom?.firstName ?? null,
              lastName: selfInRoom?.lastName ?? null,
              avatar: null,
            }
          : {
              __typename: 'User' as const,
              id: '',
              firstName: null,
              lastName: null,
              avatar: null,
            },
        photos: pendingPhotos.map((p) => ({
          __typename: 'Photo' as const,
          id: p.id,
          preview: p.preview ?? null,
          basePath: p.basePath,
        })),
      };

      await sendMessage({
        variables: { chatId: selectedChatRoom.id, text: finalText },
        optimisticResponse: {
          __typename: 'Mutation',
          sendMessage: optimisticMessage,
        },
        update(cache, { data }, { variables: vars }) {
          if (!data?.sendMessage || !vars?.chatId) {
            return;
          }
          mergeSentMessageIntoCache(cache, vars.chatId, data.sendMessage);
        },
      });

      setMessageText('');
      setPendingPhotos([]);

      track('User Action', {
        event_name: 'send_chat',
        event_type: 'active engagement',
        event_category: getChatTrackingEventCategory(undefined),
        event_subcategory: 'chat',
        event_location: 'chat_widget',
        conversation_id: selectedChatRoom.id,
        chat_id: selectedChatRoom.id,
        coach_id: coach?.id,
        message_length: finalText.length,
        has_photos: pendingPhotos.length > 0,
        photo_count: pendingPhotos.length,
      });

      scrollToLatestMessage();
    } catch (sendError) {
      datadogRum.addError(sendError);
      snackbar.show('Failed to send message. Please try again.', 5000);
    }
  }, [
    messageText,
    pendingPhotos,
    selectedChatRoom,
    sendingMessage,
    sendMessage,
    currentUser,
    track,
    coach?.id,
    scrollToLatestMessage,
    snackbar,
  ]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const handleLinkClick = useCallback(
    (url: string, messageId: string) => {
      track('User Action', {
        event_name: 'click_chat_link',
        event_type: 'screen interaction',
        event_category: getChatTrackingEventCategory(undefined),
        event_subcategory: 'chat',
        event_location: 'chat',
        event_label: url,
        conversation_id: currentChatRoom?.id,
        chat_id: currentChatRoom?.id,
        coach_id: coach?.id,
        message_id: messageId,
      });
      if (isVideoContentUrl(url)) {
        track('User Action', {
          event_name: 'video_play',
          event_type: 'screen interaction',
          event_category: 'on demand',
          event_location: 'chat',
          content_id: getVideoContentIdFromUrl(url),
        });
      }
    },
    [track, currentChatRoom?.id, coach?.id],
  );

  if (loading && !data) {
    return null;
  }
  if (error || !currentUser) {
    return null;
  }

  if (!isExpanded) {
    return (
      <ChatFab
        totalUnreadCount={totalUnreadCount}
        className={className}
        onToggle={handleToggle}
      />
    );
  }

  return (
    <Slide direction="up" in={isExpanded} mountOnEnter unmountOnExit>
      <ChatContainer className={className}>
        <ChatHeader
          chatRoom={selectedChatRoom}
          currentUserId={currentUserId}
          unreadCount={selectedChatRoom ? unreadCount : totalUnreadCount}
          onToggle={handleToggle}
          onBack={selectedChatRoom ? handleBackToList : undefined}
          isExpanded={isExpanded}
          showBackButton={!!selectedChatRoom}
        />

        {!selectedChatRoom ? (
          <ChatList
            chatRooms={chatRooms}
            loading={loading}
            currentUserId={currentUserId}
            onChatSelect={handleChatSelect}
          />
        ) : (
          <>
            <div
              style={{
                flex: 1,
                opacity: messagesVisible ? 1 : 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <ChatMessages
                messages={messages as MessageType[]}
                loading={loading}
                currentUserId={currentUserId}
                messagesEndRef={messagesEndRef}
                onLinkClick={handleLinkClick}
              />
            </div>
            <ChatInput
              messageText={messageText}
              pendingPhotos={pendingPhotos}
              uploadingPhoto={uploadingPhoto}
              photoUploadError={photoUploadError}
              sendingMessage={sendingMessage}
              fileInputRef={fileInputRef}
              onMessageChange={setMessageText}
              onSendMessage={handleSendMessage}
              onKeyPress={handleKeyPress}
              onPhotoButtonClick={handlePhotoButtonClick}
              onPhotoUpload={handlePhotoUpload}
              onRemovePhoto={handleRemovePhoto}
            />
          </>
        )}
      </ChatContainer>
    </Slide>
  );
};

export default CoachChat;
